// Barkod okutarak fatura/irsaliye satırı. Koli okutulunca satır ana birimde açılır (1 koli = 24 adet),
// fiyat ana birim başınadır (koli fiyatı / içerik). Stok, maliyet, lot ve e-fatura adet üzerinden çalışır.
import type { InvoiceItem } from '../components/types/invoice.types';
import { formatQuantityForDisplay, recalculateLineItem } from './invoiceCalculations';
import { lotFlagsFromItem } from './invoiceLot';
import type { ScannedInvoiceItem } from '../components/BarcodeScanField';

const samePackage = (a?: string, b?: string) =>
  (a ?? '').trim().toLocaleLowerCase('tr-TR') === (b ?? '').trim().toLocaleLowerCase('tr-TR');

export interface ScannedLineOptions {
  warehouseId?: string | null;
  /** Satış/irsaliye: satış fiyatı (koli fiyatı varsa ondan); alış: alış fiyatı. */
  priceSource: 'sale' | 'purchase';
  newId?: () => string;
}

/** Okutulan ürünü satırlara ekler: aynı ürün + depo + ambalaj varsa miktar bir ambalaj kadar artar. */
export function addScannedLine(items: InvoiceItem[], scanned: ScannedInvoiceItem, options: ScannedLineOptions): InvoiceItem[] {
  const { item, packageName } = scanned;
  const factor = scanned.factor > 0 ? scanned.factor : 1;
  const warehouseId = options.warehouseId || item.warehouseStocks?.[0]?.warehouseId || undefined;
  const vatRate = item.vatRate || 0;

  const enteredPrice = options.priceSource === 'purchase'
    ? (item.purchasePrice || 0)
    : (packageName && scanned.packageSalePrice != null ? scanned.packageSalePrice / factor : (item.salePrice || 0));
  const priceType: 'VAT_EXCLUDED' | 'VAT_INCLUDED' = options.priceSource === 'purchase'
    ? (item.vatStatus === 'included' && vatRate > 0 ? 'VAT_INCLUDED' : 'VAT_EXCLUDED')
    : (item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED');

  const index = items.findIndex((line) =>
    line.itemId === item.id && line.warehouseId === warehouseId && samePackage(line.packageName, packageName));
  if (index >= 0) {
    return items.map((line, i) => {
      if (i !== index) return line;
      const quantity = (line.quantity || 0) + factor;
      return recalculateLineItem({ ...line, quantity, displayQuantity: formatQuantityForDisplay(quantity) });
    });
  }

  const line: InvoiceItem = {
    id: options.newId ? options.newId() : `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    itemId: item.id,
    itemName: item.name,
    quantity: factor,
    displayQuantity: formatQuantityForDisplay(factor),
    unit: item.unit || 'adet',
    enteredPrice,
    priceType,
    unitPrice: 0,
    unitPriceWithVat: 0,
    vatRate,
    sctRate: item.sctRate || 0,
    discountRate: 0,
    discountAmount: 0,
    warehouseId,
    lineTotal: 0, netTotal: 0, vatAmount: 0, sctAmount: 0, grossTotal: 0,
    ...(packageName ? { packageName, packageFactor: factor } : {}),
    ...lotFlagsFromItem(item),
  };
  return [...items, recalculateLineItem(line)];
}

/** Miktar hücresinin altındaki not: "= 2 Koli" (tam kat) ya da "1 Koli = 24 adet". */
export function packageHint(line: Pick<InvoiceItem, 'quantity' | 'unit' | 'packageName' | 'packageFactor'>): string | null {
  if (!line.packageName || !line.packageFactor || line.packageFactor <= 0) return null;
  const count = line.quantity / line.packageFactor;
  const rounded = Math.round(count);
  if (rounded > 0 && Math.abs(count - rounded) < 1e-6) return `= ${rounded} ${line.packageName}`;
  return `1 ${line.packageName} = ${formatQuantityForDisplay(line.packageFactor)} ${line.unit || 'adet'}`;
}
